import type { Customer, CustomerInput, CustomerQuery, Page } from '@iobiz/shared'
import { api, apiFetch } from '../../api/client.js'

/**
 * Accès aux clients.
 *
 * Au Sprint 4, cette couche parlera à la réplique locale (Dexie) et non plus
 * au réseau ; les pages, elles, ne changeront pas — c'est tout l'intérêt de
 * passer par un module dédié plutôt que d'appeler `fetch` depuis les composants.
 */

function qs(params: Partial<CustomerQuery>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '' && value !== null) search.set(key, String(value))
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

export function listCustomers(
  storeId: string,
  query: Partial<CustomerQuery>,
  signal?: AbortSignal,
): Promise<Page<Customer>> {
  return api.get<Page<Customer>>(`/stores/${storeId}/customers${qs(query)}`, signal)
}

export function getCustomer(storeId: string, id: string): Promise<Customer> {
  return api.get<Customer>(`/stores/${storeId}/customers/${id}`)
}

export function listCities(storeId: string): Promise<{ data: string[] }> {
  return api.get<{ data: string[] }>(`/stores/${storeId}/customers/cities`)
}

export function createCustomer(storeId: string, input: CustomerInput): Promise<Customer> {
  return api.post<Customer>(`/stores/${storeId}/customers`, input)
}

/**
 * Mise à jour avec If-Match : le serveur refuse (409) si la fiche a changé
 * entre-temps, plutôt que d'écraser le travail d'un collègue en silence.
 */
export function updateCustomer(
  storeId: string,
  id: string,
  input: CustomerInput,
  version: number,
): Promise<Customer> {
  return apiFetch<Customer>(`/stores/${storeId}/customers/${id}`, {
    method: 'PATCH',
    body: input,
    headers: { 'If-Match': `"${version}"` },
  })
}

export function deleteCustomer(storeId: string, id: string): Promise<void> {
  return api.delete<void>(`/stores/${storeId}/customers/${id}`)
}

export interface SearchResult {
  id: string
  title: string
  subtitle: string | null
  metadata: Record<string, unknown> | null
  score: number
}

export interface SearchResponse {
  query: string
  took: number
  total: number
  groups: { type: string; total: number; results: SearchResult[] }[]
}

export function globalSearch(
  storeId: string,
  q: string,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  return api.get<SearchResponse>(
    `/stores/${storeId}/search?q=${encodeURIComponent(q)}`,
    signal,
  )
}
